'use client'
import { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  role: string;
  message: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse {
  code: number;
  message: string;
  data: Contact[];
}

// 格式化日期为YYYY-MM-DD HH:mm:ss格式
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export default function ContactList() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 获取联系人数据
  const fetchContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get<ApiResponse>(`${import.meta.env.VITE_APP_API_BASE_AI_URL}/polariscapitalai/api/contact/list`);

      if (response.data.code === 200) {
        setContacts(response.data.data);
      } else {
        setError(response.data.message || '获取数据失败');
      }
    } catch (err) {
      setError('网络请求失败，请稍后重试');
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  // 下载Excel文件
  const downloadExcel = () => {
    if (contacts.length === 0) {
      alert('没有数据可以下载');
      return;
    }

    try {
      // 准备Excel数据，使用中文表头
      const excelData = contacts.map(contact => ({
        'ID': contact.id,
        '名字': contact.firstName || '-',
        '姓氏': contact.lastName || '-',
        '邮箱': contact.email || '-',
        '电话': contact.phone || '-',
        '角色': contact.role || '-',
        '留言': contact.message || '-',
        '创建时间': formatDateTime(contact.createdAt),
        '更新时间': formatDateTime(contact.updatedAt)
      }));

      // 创建工作簿
      const wb = XLSX.utils.book_new();

      // 创建工作表
      const ws = XLSX.utils.json_to_sheet(excelData, {
        header: ['ID', '名字', '姓氏', '邮箱', '电话', '角色', '留言', '创建时间', '更新时间']
      });

      // 设置列宽（单位：字符宽度）
      ws['!cols'] = [
        { wch: 8 },  // ID列
        { wch: 12 }, // 名字列
        { wch: 12 }, // 姓氏列
        { wch: 25 }, // 邮箱列
        { wch: 15 }, // 电话列
        { wch: 12 }, // 角色列
        { wch: 30 }, // 留言列
        { wch: 20 }, // 创建时间列
        { wch: 20 }  // 更新时间列
      ];

      // 将工作表添加到工作簿
      XLSX.utils.book_append_sheet(wb, ws, '联系人列表');

      // 生成文件名
      const fileName = `联系人列表_${new Date().toISOString().slice(0, 10)}.xlsx`;

      // 导出Excel文件
      XLSX.writeFile(wb, fileName);

    } catch (err) {
      console.error('导出Excel失败:', err);
      alert('导出Excel失败，请稍后重试');
    }
  };

  // 初始加载数据
  useEffect(() => {
    fetchContacts();
  }, []);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-sm p-6">
        <h1 className="text-black text-2xl font-bold text-center mb-6">联系人列表</h1>

        {/* 操作按钮 */}
        <div className="flex justify-center space-x-4 mb-6">
          <button
            onClick={fetchContacts}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            disabled={loading}
          >
            {loading ? '刷新中...' : '刷新数据'}
          </button>
          <button
            onClick={downloadExcel}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
            disabled={contacts.length === 0}
          >
            下载Excel
          </button>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-center">
            {error}
          </div>
        )}

        {/* 联系人表格 */}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">名字</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">姓氏</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">邮箱</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">电话</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">角色</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">留言</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">创建时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {contacts.length === 0 && !loading ? (
                <tr>
                    <td colSpan={8} className="py-10 px-4 text-center text-gray-500">暂无联系人数据</td>
                  </tr>
              ) : (
                contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">{contact.id}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{contact.firstName || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{contact.lastName || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{contact.email || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{contact.phone || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">{contact.role || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 max-w-xs overflow-hidden text-ellipsis whitespace-nowrap" title={contact.message}>{contact.message || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {formatDateTime(contact.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 加载状态 */}
        {loading && (
          <div className="text-center mt-4 text-gray-500">正在加载数据...</div>
        )}
      </div>
    </div>
  );
}
